/* Drag-to-reorder for a grid, on POINTER events rather than the HTML5 drag API.
 *
 * MoveButtons chose arrows over dragging for a real reason: Expert Studio's main
 * user is a crew member on a phone, and `dragstart`/`drop` never fire on touch —
 * shipping that would have shipped a reorder the primary user cannot operate.
 * Pointer events do not have that problem: one code path covers mouse, pen and
 * finger. So dragging is back, and the arrows stay (in the photo dialog) as the
 * keyboard path, because a drag is unreachable from a keyboard no matter how it
 * is built.
 *
 * Dragging starts from a GRIP, not from the tile. On touch, a drag that begins
 * anywhere on a tile has to fight the browser for the gesture: the page wants to
 * scroll, and whoever loses does the wrong thing. `touch-action: none` on a small
 * grip settles it without making the grid itself unscrollable — and it keeps a
 * tap on the tile unambiguous, which is what opens the photo.
 *
 * Order is saved WHOLE, matching worker/galleryorder.ts and useMoveOrder: order
 * is one fact about the set, not N facts about N items. That is what lets the
 * server refuse a stale caller rather than quietly dropping someone else's photo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

export interface DragOrder {
  /** The order to render: the live preview while dragging, otherwise the real one. */
  ids: string[];
  draggingId: string | null;
  busy: boolean;
  /** Spread onto each tile's grip element. */
  gripProps: (id: string) => {
    onPointerDown: (e: React.PointerEvent) => void;
    style: { touchAction: 'none' };
  };
  /** Ref callback for each tile, so a drag can hit-test against real geometry. */
  register: (id: string, el: HTMLElement | null) => void;
}

export function useDragOrder(
  ids: readonly string[],
  save: (ids: string[]) => Promise<unknown>,
  opts: { onSaved: () => void; onError: (message: string) => void },
): DragOrder {
  const [preview, setPreview] = useState<string[] | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const tiles = useRef(new Map<string, HTMLElement>());
  /* The preview also drives the pointermove handler, which is attached once and
     would otherwise close over the first render's value. */
  const live = useRef<string[]>([]);

  const m = useMutation({
    mutationFn: save,
    onSuccess: () => opts.onSaved(),
    onError: (e) => {
      opts.onError(e instanceof Error ? e.message.replace(/^API \d+:\s*/, '') : 'Could not save the new order.');
      // The refusal carries the real order; go and read it rather than insist.
      setPreview(null);
      opts.onSaved();
    },
  });

  /* Hold the preview until the server's list agrees with it.
     Dropping it the moment the save is sent would re-render the OLD order for as
     long as the round trip takes, so every successful drag would visibly snap
     back and then correct itself. */
  useEffect(() => {
    if (!preview || draggingId) return;
    if (preview.length === ids.length && preview.every((id, i) => id === ids[i])) setPreview(null);
  }, [ids, preview, draggingId]);

  const register = useCallback((id: string, el: HTMLElement | null) => {
    if (el) tiles.current.set(id, el); else tiles.current.delete(id);
  }, []);

  const finish = useCallback((commit: boolean) => {
    const next = live.current;
    setDraggingId(null);
    live.current = [];
    const unchanged = !next.length
      || (next.length === ids.length && next.every((id, i) => id === ids[i]));
    if (!commit || unchanged) { setPreview(null); return; }
    // The preview stays until the refetch brings this same order back.
    m.mutate(next);
  }, [ids, m]);

  const onPointerDown = useCallback((id: string) => (e: React.PointerEvent) => {
    // Left button / touch / pen only — a right-click is not a drag.
    if (e.button !== 0) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    live.current = [...ids];
    setPreview([...ids]);
    setDraggingId(id);
  }, [ids]);

  useEffect(() => {
    if (!draggingId) return;

    const onMove = (e: PointerEvent) => {
      const current = live.current;
      const from = current.indexOf(draggingId);
      if (from < 0) return;

      /* Which tile is under the pointer. Hit-testing real rects rather than
         computing a column from the pointer's offset: the grid wraps, its column
         count comes from CSS, and reimplementing that maths here would be a
         second source of truth for the layout. */
      let to = -1;
      for (const [id, el] of tiles.current) {
        const i = current.indexOf(id);
        if (i < 0) continue;
        const r = el.getBoundingClientRect();
        if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
          to = i;
          break;
        }
      }
      if (to < 0 || to === from) return;

      const next = [...current];
      next.splice(to, 0, ...next.splice(from, 1));
      live.current = next;
      setPreview(next);
    };

    const onUp = () => finish(true);
    const onCancel = () => finish(false);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') finish(false); };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
    };
  }, [draggingId, finish]);

  /* While a save is in flight the preview is kept: dropping back to the server's
     order for the half-second before it answers reads as the drag failing. */
  return {
    ids: preview ?? [...ids],
    draggingId,
    busy: m.isPending,
    gripProps: (id) => ({ onPointerDown: onPointerDown(id), style: { touchAction: 'none' } }),
    register,
  };
}
